import { useMemo, useState } from "react";

// "Send now" vs. "Schedule for later" controls, shared by the campaign editor and
// the bulk Send modal. On confirm it calls onSend(sendAt) where sendAt is either
// null (send immediately) or an ISO 8601 UTC instant the API stores on the outbox.
//
// Timezone handling: the user picks a wall-clock day + time in a named IANA zone
// (default: their browser's zone). We convert that wall time in that zone to the
// correct UTC instant using Intl — no date library needed.

interface Props {
  sendNowLabel: string;                     // label for the immediate-send button
  scheduleLabel?: string;                   // label for the schedule button (default "Schedule send")
  busy?: boolean;
  disabled?: boolean;                       // e.g. nothing selected to send to
  onSend: (sendAt: string | null) => void;  // null = now; else ISO UTC instant
}

function localTz(): string {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; }
  catch { return "UTC"; }
}

// Fallback if the runtime lacks Intl.supportedValuesOf (older engines).
const TZ_FALLBACK = [
  "UTC", "Africa/Abidjan", "Africa/Cairo", "Africa/Johannesburg", "Africa/Lagos", "Africa/Nairobi",
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "America/Sao_Paulo",
  "Asia/Bangkok", "Asia/Dubai", "Asia/Kolkata", "Asia/Shanghai", "Asia/Singapore", "Asia/Tokyo",
  "Australia/Sydney", "Europe/London", "Europe/Paris", "Europe/Moscow", "Pacific/Auckland",
];

// Full IANA zone list (≈430 zones). Cached at module scope so the per-zone offset
// formatting only runs once per page load.
let ZONES_CACHE: { zone: string; region: string; label: string }[] | null = null;
function offsetLabel(now: Date, zone: string): string {
  const ms = tzOffsetMs(now, zone);
  const sign = ms < 0 ? "-" : "+";
  const abs = Math.abs(ms);
  const h = Math.floor(abs / 3600000);
  const m = Math.floor((abs % 3600000) / 60000);
  return `GMT${sign}${h}${m ? ":" + String(m).padStart(2, "0") : ""}`;
}
// Country -> primary IANA zone, so users can pick by COUNTRY (type "Algeria")
// instead of guessing the city zone. Multi-zone countries use the capital/primary.
const COUNTRY_TZ: [string, string][] = [
  // Africa
  ["Algeria","Africa/Algiers"],["Angola","Africa/Luanda"],["Benin","Africa/Porto-Novo"],
  ["Botswana","Africa/Gaborone"],["Burkina Faso","Africa/Ouagadougou"],["Burundi","Africa/Bujumbura"],
  ["Cameroon","Africa/Douala"],["Cape Verde","Atlantic/Cape_Verde"],["Central African Republic","Africa/Bangui"],
  ["Chad","Africa/Ndjamena"],["Comoros","Indian/Comoro"],["Congo (Brazzaville)","Africa/Brazzaville"],
  ["Congo (DR)","Africa/Kinshasa"],["Cote d'Ivoire","Africa/Abidjan"],["Djibouti","Africa/Djibouti"],
  ["Egypt","Africa/Cairo"],["Equatorial Guinea","Africa/Malabo"],["Eritrea","Africa/Asmara"],
  ["Eswatini","Africa/Mbabane"],["Ethiopia","Africa/Addis_Ababa"],["Gabon","Africa/Libreville"],
  ["Gambia","Africa/Banjul"],["Ghana","Africa/Accra"],["Guinea","Africa/Conakry"],
  ["Guinea-Bissau","Africa/Bissau"],["Kenya","Africa/Nairobi"],["Lesotho","Africa/Maseru"],
  ["Liberia","Africa/Monrovia"],["Libya","Africa/Tripoli"],["Madagascar","Indian/Antananarivo"],
  ["Malawi","Africa/Blantyre"],["Mali","Africa/Bamako"],["Mauritania","Africa/Nouakchott"],
  ["Mauritius","Indian/Mauritius"],["Morocco","Africa/Casablanca"],["Mozambique","Africa/Maputo"],
  ["Namibia","Africa/Windhoek"],["Niger","Africa/Niamey"],["Nigeria","Africa/Lagos"],
  ["Rwanda","Africa/Kigali"],["Sao Tome & Principe","Africa/Sao_Tome"],["Senegal","Africa/Dakar"],
  ["Seychelles","Indian/Mahe"],["Sierra Leone","Africa/Freetown"],["Somalia","Africa/Mogadishu"],
  ["South Africa","Africa/Johannesburg"],["South Sudan","Africa/Juba"],["Sudan","Africa/Khartoum"],
  ["Tanzania","Africa/Dar_es_Salaam"],["Togo","Africa/Lome"],["Tunisia","Africa/Tunis"],
  ["Uganda","Africa/Kampala"],["Zambia","Africa/Lusaka"],["Zimbabwe","Africa/Harare"],
  // Middle East
  ["United Arab Emirates","Asia/Dubai"],["Saudi Arabia","Asia/Riyadh"],["Qatar","Asia/Qatar"],
  ["Kuwait","Asia/Kuwait"],["Bahrain","Asia/Bahrain"],["Oman","Asia/Muscat"],["Israel","Asia/Jerusalem"],
  ["Jordan","Asia/Amman"],["Lebanon","Asia/Beirut"],["Iraq","Asia/Baghdad"],["Iran","Asia/Tehran"],
  // Europe
  ["United Kingdom","Europe/London"],["Ireland","Europe/Dublin"],["France","Europe/Paris"],
  ["Germany","Europe/Berlin"],["Spain","Europe/Madrid"],["Portugal","Europe/Lisbon"],["Italy","Europe/Rome"],
  ["Netherlands","Europe/Amsterdam"],["Belgium","Europe/Brussels"],["Switzerland","Europe/Zurich"],
  ["Sweden","Europe/Stockholm"],["Norway","Europe/Oslo"],["Denmark","Europe/Copenhagen"],
  ["Poland","Europe/Warsaw"],["Austria","Europe/Vienna"],["Greece","Europe/Athens"],
  ["Russia (Moscow)","Europe/Moscow"],["Turkey","Europe/Istanbul"],["Ukraine","Europe/Kyiv"],
  // Asia / Pacific
  ["India","Asia/Kolkata"],["Pakistan","Asia/Karachi"],["Bangladesh","Asia/Dhaka"],
  ["China","Asia/Shanghai"],["Hong Kong","Asia/Hong_Kong"],["Singapore","Asia/Singapore"],
  ["Malaysia","Asia/Kuala_Lumpur"],["Indonesia (Jakarta)","Asia/Jakarta"],["Thailand","Asia/Bangkok"],
  ["Vietnam","Asia/Ho_Chi_Minh"],["Philippines","Asia/Manila"],["Japan","Asia/Tokyo"],
  ["South Korea","Asia/Seoul"],["Australia (Sydney)","Australia/Sydney"],["New Zealand","Pacific/Auckland"],
  // Americas
  ["USA (Eastern)","America/New_York"],["USA (Central)","America/Chicago"],["USA (Mountain)","America/Denver"],
  ["USA (Pacific)","America/Los_Angeles"],["Canada (Toronto)","America/Toronto"],["Brazil","America/Sao_Paulo"],
  ["Mexico","America/Mexico_City"],["Argentina","America/Argentina/Buenos_Aires"],["UTC","UTC"],
];

function allZones(): { zone: string; region: string; label: string }[] {
  if (ZONES_CACHE) return ZONES_CACHE;
  let names: string[] = TZ_FALLBACK;
  try {
    const sv = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf;
    if (typeof sv === "function") names = sv("timeZone");
  } catch { /* keep fallback */ }
  if (!names.includes("UTC")) names = ["UTC", ...names];
  const now = new Date();
  ZONES_CACHE = names.map((zone) => ({
    zone,
    region: zone.includes("/") ? zone.split("/")[0] : "Other",
    label: `${zone.replace(/_/g, " ")} (${offsetLabel(now, zone)})`,
  }));
  return ZONES_CACHE;
}

// Offset (ms) of `timeZone` from UTC at the given instant. Positive east of UTC.
function tzOffsetMs(instant: Date, timeZone: string): number {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone, hourCycle: "h23",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
  const p: Record<string, string> = {};
  for (const part of dtf.formatToParts(instant)) p[part.type] = part.value;
  const asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
  // formatToParts is whole-second; `instant` may carry milliseconds. Round to the
  // nearest minute so offsets land cleanly (e.g. GMT+2, not GMT+1:59). Zones with
  // :30/:45 offsets are preserved.
  return Math.round((asUTC - instant.getTime()) / 60000) * 60000;
}

// Convert a wall-clock date/time in `timeZone` to the matching UTC instant.
function zonedWallTimeToUtc(date: string, time: string, timeZone: string): Date | null {
  if (!date || !time) return null;
  const [y, m, d] = date.split("-").map(Number);
  const [hh, mm] = time.split(":").map(Number);
  if ([y, m, d, hh, mm].some((n) => !Number.isFinite(n))) return null;
  // Treat the wall time as if it were UTC, then subtract the zone's offset at
  // roughly that instant (one correction handles all but DST-transition edges).
  const naiveUTC = Date.UTC(y, m - 1, d, hh, mm, 0);
  const offset = tzOffsetMs(new Date(naiveUTC), timeZone);
  return new Date(naiveUTC - offset);
}

// Round "now" up to the next 15 minutes, for sensible default date/time fields.
function defaultDateTime(tz: string): { date: string; time: string } {
  const now = new Date(Date.now() + 60 * 60 * 1000); // default ~1h out
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz, hourCycle: "h23",
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
  });
  const p: Record<string, string> = {};
  for (const part of fmt.formatToParts(now)) p[part.type] = part.value;
  return { date: `${p.year}-${p.month}-${p.day}`, time: `${p.hour}:00` };
}

export default function SendScheduleControls({
  sendNowLabel, scheduleLabel = "Schedule send", busy, disabled, onSend,
}: Props) {
  const tz0 = localTz();
  const [mode, setMode] = useState<"now" | "later">("now");
  const [tz, setTz] = useState(tz0);
  const init = defaultDateTime(tz0);
  const [date, setDate] = useState(init.date);
  const [time, setTime] = useState(init.time);

  // Full IANA zone list, grouped by region (Africa, America, Asia, Europe…).
  const tzGroups = useMemo(() => {
    const groups = new Map<string, { zone: string; label: string }[]>();
    for (const z of allZones()) {
      if (!groups.has(z.region)) groups.set(z.region, []);
      groups.get(z.region)!.push({ zone: z.zone, label: z.label });
    }
    for (const list of groups.values()) list.sort((a, b) => a.zone.localeCompare(b.zone));
    return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, []);

  // Pick-by-country options (sorted, with live offset) — easier than city zones.
  const countryOpts = useMemo(() => {
    const now = new Date();
    return COUNTRY_TZ
      .map(([country, zone]) => ({ country, zone, label: `${country} (${offsetLabel(now, zone)})` }))
      .sort((a, b) => a.country.localeCompare(b.country));
  }, []);

  const utc = useMemo(() => zonedWallTimeToUtc(date, time, tz), [date, time, tz]);
  const isFuture = !!utc && utc.getTime() > Date.now();

  const preview = useMemo(() => {
    if (!utc) return null;
    const txt = new Intl.DateTimeFormat("en-GB", {
      timeZone: tz, weekday: "short", day: "numeric", month: "short",
      year: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h23",
      timeZoneName: "short",
    }).format(utc);
    return txt;
  }, [utc, tz]);

  return (
    <div className="space-y-2">
      {/* Mode toggle */}
      <div className="inline-flex rounded-md border border-slate-200 p-0.5 text-xs">
        <button
          type="button"
          className={`rounded px-3 py-1 ${mode === "now" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
          onClick={() => setMode("now")}
        >Send now</button>
        <button
          type="button"
          className={`rounded px-3 py-1 ${mode === "later" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
          onClick={() => setMode("later")}
        >Schedule</button>
      </div>

      {mode === "later" && (
        <div className="space-y-2 rounded-md bg-slate-50 p-3">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium text-slate-600">Day</span>
              <input type="date" className="w-full !text-sm !py-1.5 !px-2"
                     value={date} onChange={(e) => setDate(e.target.value)} />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium text-slate-600">Time</span>
              <input type="time" className="w-full !text-sm !py-1.5 !px-2"
                     value={time} onChange={(e) => setTime(e.target.value)} />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium text-slate-600">Timezone</span>
              <select className="w-full !text-sm !py-1.5 !px-2"
                      value={tz} onChange={(e) => setTz(e.target.value)}>
                {tz0 && (
                  <optgroup label="Your timezone">
                    <option value={tz0}>{tz0.replace(/_/g, " ")} (detected)</option>
                  </optgroup>
                )}
                <optgroup label="By country">
                  {countryOpts.map((o) => <option key={"c-" + o.zone} value={o.zone}>{o.label}</option>)}
                </optgroup>
                {tzGroups.map(([region, zones]) => (
                  <optgroup key={region} label={region}>
                    {zones.map((z) => <option key={z.zone} value={z.zone}>{z.label}</option>)}
                  </optgroup>
                ))}
              </select>
            </label>
          </div>
          {preview && (
            <p className={`text-xs ${isFuture ? "text-slate-600" : "text-rose-700"}`}>
              {isFuture ? <>Will send <strong>{preview}</strong></> : "Pick a time in the future."}
            </p>
          )}
        </div>
      )}

      <div className="flex justify-end">
        {mode === "now" ? (
          <button className="btn-danger !text-xs !py-1.5 !px-3"
                  disabled={busy || disabled}
                  onClick={() => onSend(null)}>
            {busy ? "Sending…" : sendNowLabel}
          </button>
        ) : (
          <button className="btn-primary !text-xs !py-1.5 !px-3"
                  disabled={busy || disabled || !isFuture}
                  onClick={() => onSend(utc!.toISOString())}>
            {busy ? "Scheduling…" : scheduleLabel}
          </button>
        )}
      </div>
    </div>
  );
}
